import round from 'lodash/round';

import { SxProps, TooltipProps, Typography, Stack } from '@cast/design-system';
import { K8sProvider } from '@cast/shared-types';
import { getCurrencyPrecision } from '@cast/utils';

import { Money } from 'components/money';
import { NodeTypesProgressBar } from 'components/nodes';

import { NodesAnalysis } from './NodesAnalysis';

import { useCluster } from '@cast/shared-cluster-context-data-access';

export enum ValueRenderType {
  Plain = 'Plain',
  Money = 'Money',
}

type Props = {
  onDemand: number;
  spot: number;
  fallback: number;
  storage?: number;
} & Partial<{
  hideTotal: boolean;
  progressBarSx: SxProps;
  sx: SxProps;
  type: ValueRenderType;
  fullWidth: boolean;
  tooltipProps: Partial<TooltipProps>;
  showStorage: boolean;
}>;

export const NodePricingInfo = ({
  type = ValueRenderType.Money,
  showStorage = false,
  ...props
}: Props) => {
  const { cluster } = useCluster();

  const { onDemand = 0, spot = 0, fallback = 0, storage = 0 } = props;
  let total = onDemand + spot + fallback;

  if (showStorage) {
    total += storage;
  }

  const spotPercentage = (spot * 100) / (total || 1);
  const fallbackPercentage = (fallback * 100) / (total || 1);
  const storagePercentage = (storage * 100) / (total || 1);
  const precision = getCurrencyPrecision(total);

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={!props.hideTotal ? '16px' : '0px'}
      minWidth="100%"
      sx={props.sx}
    >
      {!props.hideTotal && (
        <Typography
          variant="P12B"
          color="grey.900"
          minWidth="52px"
          flexGrow={0}
        >
          <Money amount={total} precision={precision} fixed={precision} />
        </Typography>
      )}

      {Boolean(total) && (
        <NodesAnalysis
          id="node-cost-info"
          {...props}
          tooltipProps={{
            wrapIf: cluster?.providerType !== K8sProvider.ANYWHERE,
            ...props.tooltipProps,
          }}
          showStorage={showStorage}
          renderValue={(value: number) => {
            const precision = getCurrencyPrecision(value);
            switch (type) {
              case ValueRenderType.Plain: {
                return round(value, precision);
              }
              case ValueRenderType.Money: {
                return (
                  <Money
                    amount={value}
                    precision={precision}
                    fixed={precision}
                  />
                );
              }
            }
          }}
        >
          <NodeTypesProgressBar
            fallback={fallbackPercentage || 0}
            spot={spotPercentage || 0}
            storage={showStorage ? storagePercentage || 0 : undefined}
            sx={props.progressBarSx}
          />
        </NodesAnalysis>
      )}
    </Stack>
  );
};
